import React, { useState, useEffect } from 'react';
import { X, CreditCard, DollarSign, Calculator } from 'lucide-react';
import { useStaffPreferences } from '../../hooks/useStaffPreferences';

interface PaymentCalculatorEnhancedProps {
  total: number;
  onClose: () => void;
  onPaymentComplete: (paymentType: 'cash' | 'paypay', receivedAmount?: number) => void;
  onBackToOrder?: () => void; // 品目訂正用
}

const PaymentCalculatorEnhanced: React.FC<PaymentCalculatorEnhancedProps> = ({
  total,
  onClose,
  onPaymentComplete,
  onBackToOrder
}) => {
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'paypay' | null>(null);
  const [receivedAmount, setReceivedAmount] = useState<string>('');
  const [showNumpad, setShowNumpad] = useState(false);

  // 🔥 値引き機能
  const [discountType, setDiscountType] = useState<'none' | 'percent' | 'amount'>('none');
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [discountInput, setDiscountInput] = useState<string>('');

  // スタッフ個人設定を取得
  const { preferences, getExactButtonText, playSound } = useStaffPreferences();

  // クイック金額ボタン（よく使う金額）
  const quickAmounts = [1000, 2000, 3000, 5000, 10000];
  const smallAmounts = [50, 200, 500]; // 小額ボタン

  // 🔥 値引き額計算
  const calculateDiscount = () => {
    if (discountType === 'percent') {
      return Math.floor(total * (discountValue / 100));
    } else if (discountType === 'amount') {
      return Math.min(discountValue, total); // 合計を超えない
    }
    return 0;
  };

  // 🔥 値引き後の合計
  const discountedTotal = total - calculateDiscount();

  // お釣り計算（値引き後の金額で計算）
  const change = receivedAmount ? parseInt(receivedAmount) - discountedTotal : 0;

  // 現金決済処理
  const handleCashPayment = () => {
    if (!receivedAmount || parseInt(receivedAmount) < discountedTotal) {
      alert('お預かり金額が不足しています');
      return;
    }
    onPaymentComplete('cash', parseInt(receivedAmount));
  };

  // PayPay決済処理
  const handlePayPayPayment = () => {
    onPaymentComplete('paypay');
  };

  // テンキー入力処理
  const handleNumpadClick = (value: string) => {
    if (value === 'C') {
      setReceivedAmount('');
      setDiscountInput('');
    } else if (value === 'BS') {
      setReceivedAmount(prev => prev.slice(0, -1));
    } else if (value === '00') {
      // もし既に金額がある場合は加算ではなく追加
      if (receivedAmount && parseInt(receivedAmount) > 0) {
        setReceivedAmount(prev => prev + '00');
      } else {
        setReceivedAmount('00');
      }
    } else if (value === '決済確定') {
      // 決済確定ボタンが押されたとき
      if (receivedAmount && parseInt(receivedAmount) >= discountedTotal) {
        handleCashPayment();
      }
    } else {
      // 数字の入力
      if (receivedAmount && parseInt(receivedAmount) > 0) {
        // 既に金額がある場合は追加
        setReceivedAmount(prev => prev + value);
      } else {
        // 初回入力または0の場合
        setReceivedAmount(value);
      }
      // 値引き入力用にも保存
      setDiscountInput(prev => prev + value);
    }
  };

  // 🔥 値引きボタン処理
  const handleDiscountClick = (type: 'percent' | 'amount') => {
    const inputValue = parseInt(discountInput || receivedAmount);
    if (!inputValue || inputValue <= 0) {
      alert('値引き額または率を入力してください');
      return;
    }

    if (type === 'percent') {
      if (inputValue > 100) {
        alert('100%を超える値引きはできません');
        return;
      }
      setDiscountType('percent');
      setDiscountValue(inputValue);
    } else {
      if (inputValue > total) {
        alert('合計金額を超える値引きはできません');
        return;
      }
      setDiscountType('amount');
      setDiscountValue(inputValue);
    }

    // 入力値をクリア
    setReceivedAmount('');
    setDiscountInput('');
  };

  // 🔥 値引きクリア
  const handleClearDiscount = () => {
    setDiscountType('none');
    setDiscountValue(0);
    setDiscountInput('');
  };

  // 金額ボタンクリック処理（加算モード）
  const handleAmountClick = (amount: number) => {
    const currentAmount = parseInt(receivedAmount) || 0;
    setReceivedAmount((currentAmount + amount).toString());
  };

  // テンキーレイアウト（最適化版）
  const numpadLayout = [
    ['7', '8', '9'],
    ['4', '5', '6'],
    ['1', '2', '3'],
    ['00', '0', 'BS'],
    ['C', '決済確定']
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[100vh] sm:max-h-[95vh] overflow-hidden shadow-2xl">
        {/* ヘッダー */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-3 sm:p-4 flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Calculator className="text-white" size={24} />
            <h2 className="text-xl sm:text-2xl font-bold text-white">お会計</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1 sm:p-2 transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row h-[calc(100vh-60px)] sm:h-[calc(95vh-80px)] overflow-y-auto lg:overflow-hidden">
          {/* 左側：支払い方法と金額表示 */}
          <div className="flex-1 p-3 sm:p-6 bg-gray-50">
            {/* 合計金額表示（大きく見やすく） */}
            <div className="bg-white rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 shadow-md">
              {discountType !== 'none' && (
                <div className="mb-3 pb-3 border-b border-gray-200">
                  <div className="flex items-center justify-between text-gray-600">
                    <span className="text-sm sm:text-base">元の金額</span>
                    <span className="text-lg sm:text-2xl line-through">¥{total.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-red-600 mt-2">
                    <span className="text-sm sm:text-base flex items-center">
                      🎁 値引き
                      {discountType === 'percent' && ` (${discountValue}%)`}
                      <button
                        onClick={handleClearDiscount}
                        className="ml-2 text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded"
                      >
                        削除
                      </button>
                    </span>
                    <span className="text-lg sm:text-2xl font-bold">-¥{calculateDiscount().toLocaleString()}</span>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-center">
                <span className="text-base sm:text-xl text-gray-700 mr-3">お支払い金額</span>
                <span className={`text-3xl sm:text-5xl font-bold ${discountType !== 'none' ? 'text-green-600' : 'text-blue-600'}`}>
                  ¥{discountedTotal.toLocaleString()}
                </span>
              </div>
            </div>

            {/* 支払い方法選択 */}
            {!paymentMethod && (
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-700">
                  お支払い方法をお選びください
                </h3>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <button
                    onClick={() => {
                      setPaymentMethod('cash');
                      setShowNumpad(true);
                    }}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl p-4 sm:p-6 flex flex-col items-center justify-center space-y-2 sm:space-y-3 transform hover:scale-105 transition-all shadow-lg"
                  >
                    <DollarSign size={36} className="sm:w-12 sm:h-12" />
                    <span className="text-lg sm:text-2xl font-bold">現金</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('paypay')}
                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl p-4 sm:p-6 flex flex-col items-center justify-center space-y-2 sm:space-y-3 transform hover:scale-105 transition-all shadow-lg"
                  >
                    <CreditCard size={36} className="sm:w-12 sm:h-12" />
                    <span className="text-lg sm:text-2xl font-bold">PayPay</span>
                  </button>
                </div>
              </div>
            )}

            {/* 現金決済時の表示 */}
            {paymentMethod === 'cash' && (
              <div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  {/* お預かり金額入力欄（大きく見やすく） */}
                  <div className="mb-6">
                    <div className="flex items-center mb-3">
                      <span className="text-base sm:text-lg text-gray-700 mr-3">現金決済：お預かり金額</span>
                    </div>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-3xl text-gray-500">
                        ¥
                      </span>
                      <input
                        type="text"
                        value={receivedAmount ? parseInt(receivedAmount).toLocaleString() : ''}
                        readOnly
                        className="w-full pl-12 pr-4 py-4 text-3xl font-bold text-right border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none bg-gray-50"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* お釣り表示（条件付き） */}
                  {receivedAmount && parseInt(receivedAmount) >= discountedTotal && (
                    <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl p-6 border-2 border-yellow-300">
                      <div className="text-center">
                        <div className="text-sm text-gray-700 mb-2">お釣り</div>
                        <div className="text-5xl font-bold text-yellow-600">
                          ¥{change.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 不足額表示 */}
                  {receivedAmount && parseInt(receivedAmount) < discountedTotal && (
                    <div className="bg-red-50 rounded-xl p-6 border-2 border-red-300">
                      <div className="text-center">
                        <div className="text-sm text-red-700 mb-2">不足額</div>
                        <div className="text-4xl font-bold text-red-600">
                          ¥{(discountedTotal - parseInt(receivedAmount)).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* アクションボタン */}
                <div className="mt-6 flex space-x-4">
                  <button
                    onClick={() => {
                      setPaymentMethod(null);
                      setReceivedAmount('');
                    }}
                    className="flex-1 py-4 px-6 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-bold text-lg transition-all"
                  >
                    戻る
                  </button>
                  {onBackToOrder && (
                    <button
                      onClick={onBackToOrder}
                      className="flex-1 py-4 px-6 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold text-lg transition-all"
                    >
                      品目訂正
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* PayPay決済時の表示 */}
            {paymentMethod === 'paypay' && (
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-xl font-bold mb-6 text-gray-800">
                  PayPay決済
                </h3>

                <div className="bg-red-50 rounded-xl p-8 text-center mb-6">
                  <div className="mb-4">
                    <CreditCard className="mx-auto text-red-500" size={64} />
                  </div>
                  <p className="text-lg text-gray-700 mb-2">
                    お客様のPayPayアプリで
                  </p>
                  <p className="text-2xl font-bold text-red-600 mb-4">
                    ¥{total.toLocaleString()}
                  </p>
                  <p className="text-lg text-gray-700">
                    をお支払いください
                  </p>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      setPaymentMethod(null);
                    }}
                    className="flex-1 py-4 px-6 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-bold text-lg transition-all"
                  >
                    戻る
                  </button>
                  <button
                    onClick={handlePayPayPayment}
                    className="flex-1 py-4 px-6 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-lg transition-all"
                  >
                    決済完了
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 右側：テンキー（現金決済時のみ） */}
          {paymentMethod === 'cash' && showNumpad && (
            <div className="w-full lg:w-[27.6rem] p-3 sm:p-6 bg-gray-100 border-t lg:border-t-0 lg:border-l">

              {/* クイック金額ボタン */}
              <div className="mb-3 sm:mb-4">
                {/* 小額ボタン */}
                <div className="grid grid-cols-3 gap-1 sm:gap-2 lg:gap-3 mb-2">
                  {smallAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handleAmountClick(amount)}
                      className="py-2 sm:py-2.5 lg:py-3 px-1 sm:px-2 lg:px-3 bg-yellow-50 hover:bg-yellow-100 border-2 border-yellow-400 hover:border-yellow-500 rounded-lg font-bold text-xs sm:text-sm lg:text-base transition-all"
                    >
                      ¥{amount}
                    </button>
                  ))}
                </div>
                {/* 大額ボタン */}
                <div className="grid grid-cols-3 gap-1 sm:gap-2 lg:gap-3">
                  {quickAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handleAmountClick(amount)}
                      className="py-2 sm:py-3 lg:py-4 px-1 sm:px-2 lg:px-3 bg-white hover:bg-blue-50 border-2 border-gray-300 hover:border-blue-500 rounded-lg font-bold text-xs sm:text-sm lg:text-base transition-all"
                    >
                      ¥{amount.toLocaleString()}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setReceivedAmount(discountedTotal.toString());
                      playSound('click');
                    }}
                    className="py-2 sm:py-3 lg:py-4 px-1 sm:px-2 lg:px-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold text-xs sm:text-sm lg:text-base transition-all"
                  >
                    {getExactButtonText(discountedTotal)}
                  </button>
                </div>
              </div>

              {/* テンキー */}
              <div className="bg-white rounded-xl p-2 sm:p-4 lg:p-5 shadow-md">
                {numpadLayout.map((row, rowIndex) => (
                  <div key={rowIndex} className="grid grid-cols-3 gap-1 sm:gap-2 lg:gap-3 mb-1 sm:mb-2 lg:mb-3">
                    {row.map((key) => (
                      <button
                        key={key}
                        onClick={() => handleNumpadClick(key)}
                        className={`py-3 sm:py-4 lg:py-5 px-2 sm:px-3 lg:px-4 rounded-lg font-bold text-lg sm:text-xl lg:text-2xl transition-all ${
                          key === 'C'
                            ? 'col-span-1 bg-red-500 hover:bg-red-600 text-white'
                            : key === 'BS'
                            ? 'bg-orange-500 hover:bg-orange-600 text-white'
                            : key === '決済確定'
                            ? 'col-span-2 bg-blue-500 hover:bg-blue-600 text-white text-sm sm:text-base lg:text-lg'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                        }`}
                      >
                        {key === 'BS' ? '←' : key === '決済確定' ? '決済確定' : key}
                      </button>
                    ))}
                  </div>
                ))}

              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCalculatorEnhanced;
